import React, { useEffect } from 'react';
import styles from './AboutUs.module.css';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { GAUtils } from '../model/GAUtils';

const AboutUs: React.FC = () => {
  // Track page view on mount
  useEffect(() => {
    GAUtils.trackPageView('about_us', 'About XTEA - The #1 Telegram Search Engine');
  }, []);

  const handleCtaClick = (buttonName: string) => {
    GAUtils.trackButtonClick(buttonName, 'about_us');
  };

  return (
    <div className={styles.container}>
      <Helmet>
        <title>About XTEA - The #1 Telegram Search Engine</title>
        <meta name="description" content="XTEA is a solo developer project built to help you discover Telegram content. Learn the story behind the search engine." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://xtea.app/about" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About XTEA - The #1 Telegram Search Engine" />
        <meta property="og:description" content="XTEA is a solo developer project built to help you discover Telegram content. Learn the story behind the search engine." />
        <meta property="og:url" content="https://xtea.app/about" />
        <meta property="og:site_name" content="XTEA" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="About XTEA - The #1 Telegram Search Engine" />
        <meta name="twitter:description" content="Learn the story behind XTEA, the #1 Telegram search engine." />
      </Helmet>

      <div className={styles.hero}>
        <div className={styles.heroEmoji}>🔍</div>
        <h1>The XTEA Story</h1>
        <p className={styles.tagline}>
          One Developer. One Mission. Millions of Discoveries.
        </p>
      </div>

      <div className={styles.content}>
        <section className={styles.section}>
          <div className={styles.storyCard}>
            <h2>👋 Hi, I'm the creator of XTEA</h2>
            <p>
              It started with a simple frustration: I couldn't find what I was looking for on Telegram. 
              Channels, groups, bots — all scattered across the platform with no easy way to search them. 
              So I decided to build something about it.
            </p>
            <p>
              XTEA is a <strong>solo developer project</strong>, built from scratch with passion, 
              late nights, and way too much coffee. No big team, no corporate backing — just me, 
              my laptop, and a vision to make Telegram content discoverable.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2>🎯 The Problem I Set Out to Solve</h2>
          <p>
            Telegram is amazing — over 800 million users sharing incredible content every day. 
            But finding that content? That's where it gets tricky. Telegram's built-in search 
            is limited, and most third-party tools are either outdated or filled with spam.
          </p>
          <p>
            I wanted to build something <em>different</em>. A search engine that actually works. 
            One that helps you discover relevant Telegram channels, groups, and content without 
            the noise.
          </p>
        </section>

        <section className={styles.section}>
          <h2>🛠️ Built with Modern Tech</h2>
          <p>
            XTEA isn't just another weekend project. It's built with production-grade technology:
          </p>
          <div className={styles.techStack}>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>⚛️</span>
              <span>React</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>🔥</span>
              <span>Firebase</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>💳</span>
              <span>Stripe</span>
            </div>
            <div className={styles.techItem}>
              <span className={styles.techIcon}>🤖</span>
              <span>AI-Assisted</span>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2>💡 What Makes XTEA Different</h2>
          <div className={styles.values}>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>🎯</div>
              <h3>Focused on Quality</h3>
              <p>
                No ads cluttering your results. No spam. Just relevant Telegram 
                content you're actually looking for.
              </p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>⚡</div>
              <h3>Fast & Reliable</h3>
              <p>
                Built for speed. Search results load instantly so you can find 
                what you need without waiting.
              </p>
            </div>
            <div className={styles.valueCard}>
              <div className={styles.valueIcon}>🔒</div>
              <h3>Privacy First</h3>
              <p>
                Your searches are your business. I don't track, sell, or share 
                your data with anyone. Period.
              </p>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.personalNote}>
            <h2>📝 A Personal Note</h2>
            <p>
              Building XTEA has been an incredible journey. Every feature you see 
              was designed, coded, and tested by me. Every bug fixed at 2 AM, every 
              user feedback read and considered.
            </p>
            <p>
              I'm not a big company — I'm one developer who believes that good tools 
              should be accessible to everyone. If XTEA helps you discover something 
              amazing on Telegram, then all those late nights were worth it.
            </p>
            <p className={styles.signature}>
              — The Solo Developer Behind XTEA 🚀
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <h2>🚀 What's Next?</h2>
          <p>
            XTEA is constantly evolving. I'm always working on new features, improving 
            search accuracy, and making the experience better for you. Have an idea or 
            feedback? I'd love to hear from you.
          </p>
          <div className={styles.cta}>
            <Link 
              to="/" 
              className={styles.ctaButton}
              onClick={() => handleCtaClick('start_searching')}
            >
              Start Searching
            </Link>
            <a 
              href="mailto:support@xtea.app" 
              className={styles.secondaryButton}
              onClick={() => {
                handleCtaClick('get_in_touch');
                GAUtils.trackExternalLinkClick('mailto:support@xtea.app', 'Get in Touch');
              }}
            >
              Get in Touch
            </a>
          </div>
        </section>

      </div>
    </div>
  );
};

export default AboutUs; 